import { API_BASE_URL } from '../config/api'

/**
 * Resolves any of the shapes an image reference takes in this app to something
 * an <img src> can use.
 *
 * Three formats are in circulation: server-relative upload paths
 * ("/uploads/events/..."), absolute external URLs (Ticketmaster), and bare
 * base64 (older stamp photos, and GlobalEvent.flyerImage, whose schema comment
 * says "Base64 or URL"). Guessing wrong yields a broken image, so each is
 * detected explicitly.
 */
const toImageSrc = (reference) => {
  if (!reference || typeof reference !== 'string') return null

  const value = reference.trim()
  if (!value) return null

  if (value.startsWith('data:') || value.startsWith('http://') || value.startsWith('https://')) {
    return value
  }
  if (value.startsWith('/')) {
    return `${API_BASE_URL}${value}`
  }
  // No scheme and no leading slash: treat as raw base64, same as the stamp
  // photo handling below.
  return `data:image/jpeg;base64,${value}`
}

const isPdfReference = (reference) =>
  typeof reference === 'string' && /\.pdf(\?|#|$)/i.test(reference.trim())

/**
 * Best available thumbnail for an event in a SEARCH result.
 *
 * Deliberately flyer-first, which is the opposite of getEventImage below: when
 * you are picking "which Bass Canyon is this", the flyer is the thing you
 * recognise. Once an event is in your passport, your own photos matter more.
 *
 * @returns {{src: string|null, type: 'flyer'|'pdf'|'stamp'|'media'|'external'|null}}
 */
export const getEventThumbnail = (event) => {
  if (!event) return { src: null, type: null }

  if (event.flyerImage) {
    // PDF flyers can't render in an <img> — /uploads serves them with
    // Content-Disposition: attachment (see INLINE_SAFE_EXTENSIONS in
    // server.js). The caller shows a document badge instead.
    if (isPdfReference(event.flyerImage)) {
      return { src: toImageSrc(event.flyerImage), type: 'pdf' }
    }
    const src = toImageSrc(event.flyerImage)
    if (src) return { src, type: 'flyer' }
  }

  // Ticketmaster results carry their own image array rather than a flyer.
  if (Array.isArray(event.images) && event.images.length > 0) {
    // Prefer a wide, reasonably large image; these come in many crops.
    const preferred = event.images.find(img => img.ratio === '16_9' && img.width >= 500)
      || event.images.find(img => img.width >= 500)
      || event.images[0]
    const src = toImageSrc(preferred?.url)
    if (src) return { src, type: 'external' }
  }

  const stamp = toImageSrc(event.stampPhoto)
  if (stamp) return { src: stamp, type: 'stamp' }

  if (Array.isArray(event.media)) {
    const imageMedia = event.media.find(m => m.mimetype?.startsWith('image/'))
    if (imageMedia) return { src: toImageSrc(imageMedia.path), type: 'media' }
  }

  return { src: null, type: null }
}

export const getEventImage = (event) => {
  // Use selected default photo if available
  if (event.defaultPhoto) {
    if (event.defaultPhoto === 'stamp' && event.stampPhoto) {
      // Ensure base64 images have proper data URL prefix
      if (event.stampPhoto.startsWith('data:')) {
        return event.stampPhoto;
      } else {
        return `data:image/jpeg;base64,${event.stampPhoto}`;
      }
    } else if (event.defaultPhoto !== 'stamp' && event.media) {
      // Find the specific media item
      const selectedMedia = event.media.find(m => m._id === event.defaultPhoto);
      if (selectedMedia) {
        return `${API_BASE_URL}${selectedMedia.path}`;
      }
    }
  }

  // Fallback to original logic: prioritize stamp photo if available
  if (event.stampPhoto) {
    // Ensure base64 images have proper data URL prefix
    if (event.stampPhoto.startsWith('data:')) {
      return event.stampPhoto;
    } else {
      return `data:image/jpeg;base64,${event.stampPhoto}`;
    }
  }

  // Fallback to regular media
  if (event.media && event.media.length > 0) {
    const imageMedia = event.media.find(m => m.mimetype.startsWith('image/'));
    if (imageMedia) {
      return `${API_BASE_URL}${imageMedia.path}`;
    }
  }

  // Last resort: the event flyer. Only reached when the card would otherwise
  // render an empty placeholder — every event that already resolves to a photo
  // above keeps exactly the photo it had. Upcoming events have no stamp and no
  // media yet, so without this the whole upcoming half of a passport is grey.
  if (event.flyerImage && !isPdfReference(event.flyerImage)) {
    return toImageSrc(event.flyerImage);
  }

  return null;
};

export const getAllEventPhotos = (event) => {
  const photos = [];

  // Add stamp photo if available
  if (event.stampPhoto) {
    const stampPhotoUrl = event.stampPhoto.startsWith('data:')
      ? event.stampPhoto
      : `data:image/jpeg;base64,${event.stampPhoto}`;
    photos.push({
      id: 'stamp',
      url: stampPhotoUrl,
      name: 'Stamp Photo',
      type: 'stamp'
    });
  }

  // Add media photos
  if (event.media && event.media.length > 0) {
    event.media.forEach(media => {
      if (media.mimetype.startsWith('image/')) {
        photos.push({
          id: media._id,
          url: `${API_BASE_URL}${media.path}`,
          name: media.customName || media.originalName,
          type: 'media'
        });
      }
    });
  }

  return photos;
};